/** QL-556 — the one definition of a recurring portfolio's default months. */
export const DEFAULT_RECURRING_HORIZON_YEARS = 10;

/**
 * First day of the current month, UTC midnight. The month is read in UTC on
 * every surface (API and browser alike) so the month the widget advertises is
 * the month the orders are written with, whatever the host or user zone.
 */
export function defaultScheduleStartDate(now: Date = new Date()): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
}

/** Default end: `start` + DEFAULT_RECURRING_HORIZON_YEARS, same month. */
export function defaultScheduleEndDate(start: Date): Date {
  return new Date(
    Date.UTC(start.getUTCFullYear() + DEFAULT_RECURRING_HORIZON_YEARS, start.getUTCMonth(), 1),
  );
}

export type RecurringScheduleDates = {
  recurringStartDate?: string | Date | null;
  recurringEndDate?: string | Date | null;
};

/**
 * Parse a stored/explicit schedule date. Zone-less strings ("YYYY-MM-DD" and the
 * DB timestamp form "YYYY-MM-DD HH:MM:SS") are UTC wall time, never host-local,
 * so a stored value is never shifted. Null/unparsable gives null.
 */
export function parseScheduleDate(value: string | Date | null | undefined): Date | null {
  if (value == null) return null;
  let d: Date;
  if (value instanceof Date) {
    d = value;
  } else {
    const v = value.trim();
    const zoneLess = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(v);
    d = new Date(zoneLess ? `${v.replace(" ", "T")}Z` : v);
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * QL-688 fix round 2 — the UTC calendar month/year of a stored/explicit
 * schedule date, for display (e.g. a savings-plan card's start/end month).
 * Always parses zone-less DB strings as UTC first (via `parseScheduleDate`)
 * before reading the month/year, so the displayed month never shifts with
 * the browser's timezone. `month` is 0-indexed (`Date#getUTCMonth`).
 * Null/unparsable gives null.
 */
export function scheduleMonthParts(
  value: string | Date | null | undefined,
): { year: number; month: number } | null {
  const d = parseScheduleDate(value);
  return d ? { year: d.getUTCFullYear(), month: d.getUTCMonth() } : null;
}

/**
 * Effective dates of a recurring schedule: explicit values win; an unset or
 * unparsable start falls back to the current month, an unset end to the
 * EFFECTIVE start + 10 years (so "start Jan 2027, no end" ends Jan 2037).
 */
export function resolveRecurringScheduleDates(
  schedule: RecurringScheduleDates,
  now: Date = new Date(),
): { startDate: Date; endDate: Date; startDefaulted: boolean; endDefaulted: boolean } {
  const explicitStart = parseScheduleDate(schedule.recurringStartDate);
  const explicitEnd = parseScheduleDate(schedule.recurringEndDate);
  const startDate = explicitStart ?? defaultScheduleStartDate(now);
  const endDate = explicitEnd ?? defaultScheduleEndDate(startDate);
  return { startDate, endDate, startDefaulted: !explicitStart, endDefaulted: !explicitEnd };
}

/**
 * True when an explicit end month falls before the effective start — the
 * explicit start, or the default (current) month when the start is unset. An
 * unset end is never "before" anything. This is the completion rule that
 * keeps a defaulted start from landing after the advisor's end month.
 */
export function recurringEndBeforeStart(
  schedule: RecurringScheduleDates,
  now: Date = new Date(),
): boolean {
  const end = parseScheduleDate(schedule.recurringEndDate);
  if (!end) return false;
  const start = parseScheduleDate(schedule.recurringStartDate) ?? defaultScheduleStartDate(now);
  return end.getTime() < start.getTime();
}

/**
 * QL-688 — the one grammar a schedule month input must have. Postgres-tight:
 * everything that passes is a value `timestamp` accepts, so a 500 is impossible.
 * Hour/minute/second and the UTC offset are captured (groups 4-9) so
 * `normalizeScheduleMonth` can range-check them — the shape alone would also
 * accept "25:99" or a "+99:99" offset. The offset may be `Z`, `±HH:MM`,
 * `±HHMM` or `±HH`, and `T`/`Z` may be lowercase — every form Postgres and
 * `Date` accepted before QL-688, so only the documented breaks (>3 fractional
 * digits, the year bound, impossible dates/times) are new rejections.
 */
const SCHEDULE_MONTH_INPUT =
  /^(\d{4})-(\d{2})-(\d{2})(?:[Tt ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(?:[Zz]|([+-])(\d{2})(?::?(\d{2}))?)?)?$/;
export const SCHEDULE_MONTH_MIN_YEAR = 1900;
export const SCHEDULE_MONTH_MAX_YEAR = 2999;
/** The input rule, worded for error messages and API docs. */
export const SCHEDULE_MONTH_INPUT_HINT =
  "a calendar date (YYYY-MM-DD, or an ISO 8601 datetime); the month is stored as its first day, 00:00 UTC — the day, time of day and UTC offset are ignored";

/**
 * Normalise a schedule month input to the first of its month, 00:00 UTC, as an
 * ISO string ("2026-11-01T00:00:00.000Z"). The calendar date is taken AS
 * WRITTEN: day, time of day and UTC offset are ignored, never converted — so
 * "2026-11-01T00:00:00+01:00" and "2026-11-01" are both November, and the DB
 * echo "2026-11-01 00:00:00" round-trips. Null for anything else (non-string,
 * '2026-11', 'Nov 2026', 2026-02-30, year outside 1900-2999); the caller
 * decides whether an absent value is allowed.
 */
export function normalizeScheduleMonth(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const m = SCHEDULE_MONTH_INPUT.exec(value.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (year < SCHEDULE_MONTH_MIN_YEAR || year > SCHEDULE_MONTH_MAX_YEAR) return null;
  if (month < 1 || month > 12 || day < 1) return null;
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null;
  // The day/time/offset are discarded from the stored value, but a value that
  // carries a nonsensical time or offset is still not a date — "25:99" or
  // "+99:99" must 400, not silently normalise (QL-688 review fix).
  if (m[4] !== undefined) {
    const hour = Number(m[4]);
    const minute = Number(m[5]);
    if (hour > 23 || minute > 59) return null;
    if (m[6] !== undefined && Number(m[6]) > 59) return null;
  }
  if (m[7] !== undefined) {
    const offsetHour = Number(m[8]);
    const offsetMinute = m[9] === undefined ? 0 : Number(m[9]);
    if (offsetHour > 14 || offsetMinute > 59) return null;
  }
  return new Date(Date.UTC(year, month - 1, 1)).toISOString();
}
